/**
 * Phase 5.9 Stage 2 — Active Carts pill + drawer.
 *
 * Floating "ACTIVE CARTS: N" button in the dispatcher header. Pulses red when
 * any cart is abandoned (>30 min since last touch). Clicking opens a side
 * drawer with the full list:
 *   - Customer name + 🟢 watching #ORD-XXX badge if applicable
 *   - Item lines (name, size, qty)
 *   - Subtotal · item count · region badge
 *   - Time-since-last-touch ("12m ago") · status pill (ACTIVE / ABANDONED)
 *   - Tap-to-call (tel: link) + "View customer profile" quick actions
 *
 * Live updates: subscribes to `cart.synced`, `cart.cleared` SSE events with
 * region scope. Debounced refetch + 30s poll fallback.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ShoppingCart, AlertTriangle, Eye, Phone, RefreshCcw, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { useSSE } from '../hooks/useSSE';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

function fmtAge(seconds) {
  if (seconds == null) return '—';
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function fmtMoney(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

export function ActiveCartsPanel({ isSuperAdmin, selectedRegion }) {
  const [carts, setCarts] = useState([]);
  const [counts, setCounts] = useState({ total: 0, abandoned: 0, watching: 0, active: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const regionParam = useMemo(() => {
    if (!isSuperAdmin) return '';
    return selectedRegion === 'all' ? 'all' : selectedRegion;
  }, [isSuperAdmin, selectedRegion]);

  const fetchCarts = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '100');
      if (regionParam) qs.set('region', regionParam);
      const { data } = await axios.get(
        `${API_URL}/api/admin/active-carts?${qs.toString()}`,
        { withCredentials: true },
      );
      setCarts(data.carts || []);
      setCounts(data.counts || { total: 0, abandoned: 0, watching: 0, active: 0 });
    } catch (_e) {
      // silent — supplementary info
    } finally {
      setLoading(false);
    }
  }, [regionParam]);

  // Initial + region-change refetch
  useEffect(() => { fetchCarts(); }, [fetchCarts]);

  // SSE-driven debounced refresh on cart events
  const fetchRef = useRef(fetchCarts);
  fetchRef.current = fetchCarts;
  const debounceRef = useRef(null);
  useSSE(useCallback((evt) => {
    if (!evt || !evt.type) return;
    if (evt.type !== 'cart.synced' && evt.type !== 'cart.cleared') return;
    if (debounceRef.current) return;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      fetchRef.current?.();
    }, 800);
  }, []));

  // 30s poll fallback so age_minutes stays fresh + abandonment ticks over
  useEffect(() => {
    const t = setInterval(fetchCarts, 30_000);
    return () => clearInterval(t);
  }, [fetchCarts]);

  const hasAbandoned = counts.abandoned > 0;
  const hasWatching = counts.watching > 0;

  // The visible pill — sits in the dispatcher header
  const pill = (
    <button
      type="button"
      data-testid="active-carts-pill"
      onClick={() => setOpen(true)}
      title={
        counts.total === 0
          ? 'No active carts'
          : `${counts.total} active cart${counts.total === 1 ? '' : 's'}`
            + (hasAbandoned ? ` · ${counts.abandoned} abandoned` : '')
            + (hasWatching ? ` · ${counts.watching} watching an order` : '')
      }
      className={[
        'flex items-center gap-2 px-3 py-1 border font-mono text-xs uppercase rounded-none transition-colors',
        hasAbandoned
          ? 'border-[#FF3B30] text-[#FF3B30] bg-[rgba(255,59,48,0.08)] hover:bg-[rgba(255,59,48,0.18)]'
          : counts.total > 0
            ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.06)] hover:bg-[rgba(57,255,20,0.16)]'
            : 'border-[#4B6B52] text-[#4B6B52] hover:border-[#39FF14] hover:text-[#39FF14]',
        hasAbandoned ? 'animate-pulse' : '',
      ].join(' ')}
    >
      <ShoppingCart className="w-3.5 h-3.5" />
      <span>CARTS</span>
      <span
        className="px-1.5 py-0.5 bg-black/40 border border-current text-[10px] font-bold"
        data-testid="active-carts-pill-count"
      >
        {counts.total}
      </span>
      {hasAbandoned && (
        <Badge
          variant="outline"
          className="border-[#FF3B30] text-[#FF3B30] text-[10px] px-1 py-0 flex items-center gap-0.5"
          data-testid="active-carts-pill-abandoned"
          title={`${counts.abandoned} abandoned (no activity 30+ min)`}
        >
          <AlertTriangle className="w-2.5 h-2.5" />
          {counts.abandoned}
        </Badge>
      )}
    </button>
  );

  return (
    <>
      {pill}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          hideCloseButton
          className="bg-[#050003] border-[var(--terminal-border,#2A0F3D)] text-[var(--terminal-text,#A3C7A8)] w-full sm:max-w-md overflow-y-auto"
          data-testid="active-carts-drawer"
        >
          <SheetHeader>
            <SheetTitle className="text-[#39FF14] font-mono">
              <ShoppingCart className="w-4 h-4 inline-block mr-2 -mt-0.5" />
              ACTIVE CARTS
            </SheetTitle>
            <SheetDescription className="font-mono text-xs">
              Customers with a non-empty cart in the last 24h.
              <br />
              Abandoned = no activity for 30+ minutes.
            </SheetDescription>
          </SheetHeader>

          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-1 my-4 font-mono text-[10px]">
            <div
              className="px-2 py-2 rounded bg-black/40 border border-[var(--terminal-border)] text-center"
              data-testid="active-carts-count-total"
            >
              <div className="text-[#39FF14] text-base">{counts.total}</div>
              <div className="text-[#888]">total</div>
            </div>
            <div
              className={`px-2 py-2 rounded bg-black/40 border text-center ${hasAbandoned ? 'border-[#FF3B30]' : 'border-[var(--terminal-border)]'}`}
              data-testid="active-carts-count-abandoned"
            >
              <div className={`text-base ${hasAbandoned ? 'text-[#FF3B30]' : 'text-[#888]'}`}>
                {counts.abandoned}
              </div>
              <div className="text-[#888]">abandoned</div>
            </div>
            <div
              className={`px-2 py-2 rounded bg-black/40 border text-center ${hasWatching ? 'border-[#FFD700]' : 'border-[var(--terminal-border)]'}`}
              data-testid="active-carts-count-watching"
            >
              <div className={`text-base ${hasWatching ? 'text-[#FFD700]' : 'text-[#888]'}`}>
                {counts.watching}
              </div>
              <div className="text-[#888]">👀 watching</div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-2 font-mono text-[10px] text-[#888]">
            <span>// auto-refresh every 30s</span>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchCarts}
              disabled={loading}
              className="h-7 px-2"
              data-testid="active-carts-refresh"
            >
              <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          <div className="space-y-2 pb-24" data-testid="active-carts-list">
            {carts.length === 0 ? (
              <div className="text-center text-[#888] text-sm py-12 font-mono">
                {loading ? '// loading…' : '// no active carts'}
              </div>
            ) : carts.map((c) => (
              <CartRow key={c.user_id} cart={c} />
            ))}
          </div>

          {/* Sticky bottom CLOSE — mobile thumb-friendly. Safe-area aware. */}
          <div
            className="sticky bottom-0 left-0 right-0 -mx-6 px-6 pt-3 bg-gradient-to-t from-[#050003] via-[#050003] to-transparent border-t border-[var(--terminal-border,#2A0F3D)]"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            <Button
              type="button"
              onClick={() => setOpen(false)}
              variant="outline"
              className="w-full h-11 border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] hover:text-[#FF3B30] rounded-none font-mono uppercase tracking-wider"
              data-testid="active-carts-close-bottom"
            >
              ✕ CLOSE
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function CartRow({ cart }) {
  const {
    user_id, customer_name, customer_email, customer_phone,
    items = [], subtotal, item_count, age_seconds, abandoned,
    watching_order_id, region,
  } = cart;

  return (
    <div
      data-testid={`active-cart-row-${user_id}`}
      className={[
        'rounded border p-3 font-mono text-xs',
        abandoned
          ? 'border-[#FF3B30] bg-[rgba(255,59,48,0.04)]'
          : 'border-[var(--terminal-border)] bg-black/40',
      ].join(' ')}
    >
      {/* Header row */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Link
          to={`/dispatch/admin/users?openUser=${user_id}&from=active-carts`}
          className="text-[#39FF14] font-bold truncate min-w-0 flex-1 hover:underline group flex items-center gap-1.5"
          data-testid={`active-cart-open-user-${user_id}`}
          title="Open customer profile"
        >
          <span className="truncate">{customer_name || customer_email || user_id.slice(0, 8)}</span>
          <ArrowUpRight className="w-3 h-3 opacity-60 group-hover:opacity-100 shrink-0" />
        </Link>
        {region && (
          <Badge variant="outline" className="text-[10px] px-1 py-0">{region}</Badge>
        )}
        {abandoned ? (
          <Badge
            className="bg-[#FF3B30] text-black text-[10px] px-1.5 py-0"
            data-testid={`active-cart-abandoned-${user_id}`}
          >
            <AlertTriangle className="w-3 h-3 mr-1 inline-block -mt-0.5" />
            ABANDONED
          </Badge>
        ) : (
          <Badge variant="outline" className="border-[#39FF14] text-[#39FF14] text-[10px] px-1.5 py-0">
            ACTIVE
          </Badge>
        )}
      </div>

      {/* Watching badge */}
      {watching_order_id && (
        <div
          className="flex items-center gap-1 mb-2 px-2 py-1 rounded bg-[rgba(255,215,0,0.08)] border border-[#FFD700] text-[#FFD700] text-[10px]"
          data-testid={`active-cart-watching-${user_id}`}
        >
          <Eye className="w-3 h-3" />
          Watching order #{String(watching_order_id).slice(0, 8).toUpperCase()}
        </div>
      )}

      {/* Items */}
      <div className="space-y-0.5 mb-2 text-[#A3C7A8]">
        {items.slice(0, 5).map((it, idx) => (
          <div key={idx} className="flex justify-between gap-2">
            <span className="truncate min-w-0">
              {it.name}{it.size ? ` · ${it.size}` : ''} <span className="text-[#666]">×{it.qty}</span>
            </span>
            <span className="text-[#888] whitespace-nowrap">{fmtMoney(it.unit_price * it.qty)}</span>
          </div>
        ))}
        {items.length > 5 && (
          <div className="text-[#666] text-[10px]">+ {items.length - 5} more line{items.length - 5 === 1 ? '' : 's'}</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-[var(--terminal-border)] text-[10px]">
        <span className="text-[#888]">
          {item_count} item{item_count === 1 ? '' : 's'} · last touched {fmtAge(age_seconds)}
        </span>
        <span className="text-[#39FF14] font-bold">{fmtMoney(subtotal)}</span>
      </div>

      {/* Quick actions */}
      {customer_phone && (
        <div className="flex gap-1 mt-2">
          <a
            href={`tel:${customer_phone}`}
            data-testid={`active-cart-call-${user_id}`}
            className="flex-1 text-center px-2 py-1 border border-[var(--terminal-border)] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14] text-[10px] rounded transition-colors"
          >
            <Phone className="w-3 h-3 inline-block mr-1 -mt-0.5" />
            CALL {customer_phone}
          </a>
        </div>
      )}
    </div>
  );
}
